import SpinnerIcon from '@components/icons/spinner-icon';
import { getAccountLoginError } from '@crocodille/bbdomu-i18n';
import { v2_useAuthorizedCustomerAction } from '@crocodille/common';
import { useRouter } from '@framework/router';
import * as routing from '@routing';
import { getSession, signIn } from 'next-auth/react';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { analyticsEvent } from '../../../modules/analytics';
import { ACCOUNT_PASSWORD_RESET, ACCOUNT_REGISTER } from '../../../routing';
import Button from '../../elements/button/button';
import ErrorEl from '../../elements/error/error';
import Input from '../../elements/input-2/input-2';
import Success from '../../elements/success/success';
import { AppleButton, FacebookButton, GoogleButton } from '../social/social';

import styles from './account-login-form.module.scss';

export interface AccountLoginProps {
  readonly enabledProviders?: readonly string[];
  readonly callbackUrl?: string;
  readonly error?: string;
}

const AccountLoginForm: React.FC<
  React.PropsWithChildren<AccountLoginProps>
> = ({ enabledProviders = [], callbackUrl, error: externalError }) => {
  const { t } = useTranslation();

  const [success, setSuccess] = useState<string>();

  const { push } = useRouter();

  const {
    trigger,
    isMutating: pending,
    error: actionError,
  } = v2_useAuthorizedCustomerAction(
    async (credentials: { email: string; password: string }) => {
      const signInResponse = await signIn('credentials', {
        callbackUrl,
        ...credentials,
        redirect: false,
      });

      if (signInResponse?.error) {
        throw new Error(signInResponse.error);
      }

      return undefined;
    },
    {
      onSuccess: async () => {
        const session = await getSession();

        analyticsEvent('login', {
          user_id: session?.user?.id ?? '',
        });

        setSuccess(t('account.login_success'));

        if (callbackUrl) {
          push(callbackUrl);
        } else {
          push(routing.menu());
        }
      },
      revalidate: true,
    },
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const form = e.currentTarget;

      const formData = new FormData(form);

      const { email, password } = Object.fromEntries(formData.entries());
      await trigger({ email: email.toString(), password: password.toString() });
    },
    [trigger],
  );

  const error = getAccountLoginError(actionError?.message ?? externalError);

  return (
    <div className={styles.wrapper}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <h1 className={styles.formTitle}>{t('account.login_title')}</h1>
        <p className={styles.formText}>{t('account.login_text')}</p>
        {enabledProviders.includes('facebook') ? (
          <FacebookButton onClick={() => signIn('facebook')} />
        ) : null}
        {enabledProviders.includes('google') ? (
          <GoogleButton onClick={() => signIn('google')} />
        ) : null}
        {enabledProviders.includes('apple') ? (
          <AppleButton onClick={() => signIn('apple')} />
        ) : null}
        {enabledProviders.length > 0 ? (
          <div className={styles.loginDivider}>
            <span>{t('account.login_or')}</span>
          </div>
        ) : null}
        <div className={styles.formWrapper}>
          <Input
            name="email"
            type="email"
            required
            placeholder={t('account.email')}
            autoComplete="username"
          />
          <Input
            name="password"
            type="password"
            required
            autoComplete="current-password"
            placeholder={t('account.password')}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button type="submit" round>
            {pending ? <SpinnerIcon /> : null}
            {t('account.login')}
          </Button>
        </div>
        {error && (
          <ErrorEl>
            <Trans t={t} i18nKey={`account.error.${error}`}></Trans>
          </ErrorEl>
        )}
        {success && <Success>{success}</Success>}
      </form>

      <Link
        href={ACCOUNT_PASSWORD_RESET}
        prefetch={false}
        className={styles.forgetLink}
      >
        {t('account.login_forget')}
      </Link>
      <Link
        href={ACCOUNT_REGISTER}
        prefetch={false}
        className={styles.registerLink}
      >
        {t('account.login_register')}
      </Link>
    </div>
  );
};

export default AccountLoginForm;
